import React, { useRef, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import Button from 'src/components/Button'
import Captcha, { type ICaptchaRef } from 'src/components/Captcha'
import LightBackground from 'src/components/LightBackground'
import { useAppDispatch, useAppSelect } from 'src/hooks/useRedux'
import PatternIcon from 'src/icons/PatternIcon'
import { validateNino } from 'src/utils/validateNino'

import { getCard } from '../../ducks/home.actions'
import { setPayload } from '../../ducks/home.reducer'
import TextContent from '../TextContent'

import FormHeader from './FormHeader'
import LastName from './LastName'
import NationalInsuranceNo from './NationalInsuranceNo'
import RegistrationNumber from './RegistrationNumber'
import Scheme from './SchemeInput'

const SmartCheckForm: React.FC = () => {
  const [showInfo, setInfoState] = useState<boolean>(() => false)
  const { payload, loading, error } = useAppSelect((state) => state.home)
  const dispatch = useAppDispatch()
  const captchaRef = useRef<ICaptchaRef>(null)
  const showNiInput = ['C4T', 'YXO'].includes(payload.schemeIdentifier)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (showNiInput) {
      if (!payload.cardSerialNumber && !payload.nino) {
        alert('Please enter either Registration number or National Insurance No')
        return
      }
      if (payload.nino && !validateNino(payload.nino)) {
        alert('Please enter a valid National Insurance Number, like AB123456C')
        return
      }
    } else {
      if (!payload.cardSerialNumber) {
        alert('Please enter Registration number')
        return
      }
    }

    if (!payload.captchaToken) {
      alert('Please validate captcha')
      return
    }

    const { cardSerialNumber, nino, ...restPayload } = payload
    const apiPayload = {
      ...restPayload,
      ...(cardSerialNumber && { cardSerialNumber }),
      ...(nino && { nino }),
    }

    dispatch(getCard(apiPayload))

    resetForm()
  }

  const resetForm = () => {
    captchaRef.current?.resetCaptcha?.()
    dispatch(setPayload({ ...payload, captchaToken: '' }))
  }

  return (
    <LightBackground>
      <FormHeader
        setInfoState={() => {
          setInfoState(true)
        }}
      />
      <Box
        component="div"
        sx={{ position: 'relative', width: '100%', pointerEvents: 'none' }}
      >
        <PatternIcon
          sx={{
            position: 'absolute',
            width: 340,
            height: 720,
            left: 0,
            top: 0,
            translate: '0 -49.7%',
            pointerEvents: 'none',
          }}
        />
      </Box>
      <form onSubmit={onSubmit} style={{ width: '70%', maxWidth: '50%', zIndex: 9 }}>
        <Stack justifyContent="center" alignItems="center" mt={10} spacing={10}>
          {showInfo ? (
            <Stack direction="row" spacing={10} sx={{ width: '100%' }}>
              <TextContent
                onClose={() => {
                  setInfoState(false)
                }}
              />
            </Stack>
          ) : showNiInput ? (
            // CSCS/LISS layout
            <>
              <Stack direction="row" spacing={20} sx={{
                width: '100%',
                '@media (max-width: 1000px)': {
                  flexDirection: 'column',
                },
              }}>
                <Scheme />
                <Box sx={{
                  width: '100%',
                  '@media (max-width: 1000px)': {
                    marginLeft: '22px !important',
                  },
                }} ><LastName /></Box>
              </Stack>
              <Box sx={{
                border: '2px solid',
                borderColor: 'grey.400',
                borderRadius: 1,
                p: 10,
                width: '100%',
                backgroundColor: 'rgba(245, 245, 245, 0.7)',
                '@media (max-width: 1000px)': {
                  marginLeft: '47px !important',
                },
              }}>
                <Typography sx={{ mb: 2 }}>Enter at least one identifier <span style={{ color: '#F90000' }}>*</span></Typography>
                <Stack direction="row" alignItems="center" spacing={4} sx={{
                  '@media (max-width: 1000px)': {
                    flexDirection: 'column',
                  },
                }}>
                  <Box sx={{
                    flex: 1,
                    width: '100%',
                    pl: 10,
                    '@media (max-width: 1000px)': {
                      marginRight: '20px !important',
                    },
                  }}><RegistrationNumber /></Box>
                  <Typography sx={{ pt: 7, px: 2 }}>OR</Typography>
                  <Box sx={{
                    flex: 1,
                    width: '100%',
                    pr: 10,
                    '@media (max-width: 1000px)': {
                      marginRight: '20px !important',
                      marginLeft: '47px !important',
                    },
                  }}><NationalInsuranceNo /></Box>
                </Stack>
              </Box>
            </>
          ) : (
            // Default layout for other schemes
            <Stack direction="row" spacing={10} sx={{
              width: '100%',
              '@media (max-width: 1000px)': {
                flexDirection: 'column',
              },
            }}>
              <Scheme />
              <RegistrationNumber />
              <LastName />
            </Stack>
          )}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              width: '100%',
              '@media (max-width: 1000px)': {
                width: '100%',
                marginLeft: '47px !important',
                flexDirection: 'column', // Switch to block layout for small screens
              },
            }}
          >
            <Captcha
              ref={captchaRef}
              onChange={(token) => {
                dispatch(setPayload({ ...payload, captchaToken: token || '' }))
              }}
              isLoading={loading}
            />

            <Button
              title={loading ? 'Loading' : 'Check Card'}
              type="submit"
              disabled={loading}
              sx={{
                '@media (max-width: 1000px)': {
                  marginRight: 'auto',
                  marginTop: '20px'
                },
              }}
            />
          </Stack>

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </form>
    </LightBackground >
  )
}

export default SmartCheckForm
